import React, { CSSProperties } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import UserProfileDropdown from './UserProfileDropdown';
import { LAYOUT_CONSTANTS } from '../hooks/useLayout';
import { useNavigationHistory } from '@/shared/context/useNavigationHistory';

interface BreadcrumbItem {
  label: string;
  path: string;
  navigateTo: string;
}

const MenuIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 5L20 5M4 11H20M4 17H12"
      stroke="#474A4C"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const routeLabels: Record<string, string> = {
  '/': 'Panel główny',
  '/Panel-główny': 'Panel główny',
  '/administrators': 'Administratorzy',
  '/buildings': 'Klienci',
  '/Klienci': 'Klienci',
  '/technicians': 'Technicy',
  '/subscriptions': 'Subskrypcje',
  '/activities': 'Aktywności',
  '/qms': 'QMS',
  '/Profil': 'Profil',
  '/Placówki': 'Placówki',
  '/Placówki/Dodaj-placówkę': 'Dodaj placówkę',
  '/Typy-usług': 'Typy usług',
  '/Typy-usług/Dodaj-typ-usługi': 'Dodaj typ usługi',
  '/Certyfikaty': 'Certyfikaty',
};

// Segment-level labels for breadcrumb generation
const segmentLabels: Record<string, string> = {
  administrators: 'Administratorzy',
  addUser: 'Dodaj Administratora',
  'Edycja-Administratora': 'Edytuj Administratora',
  'Podgląd-Administratora': 'Podgląd Administratora',
  buildings: 'Klienci',
  Klienci: 'Klienci',
  'Dodaj-klienta': 'Dodaj klienta',
  'Podgląd-klienta': 'Podgląd klienta',
  'Edycja-klienta': 'Edytuj klienta',
  'Edytuj-klienta': 'Edytuj klienta',
  technicians: 'Technicy',
  addTechnician: 'Dodaj technika',
  'Podgląd-technika': 'Podgląd technika',
  'Edycja-technika': 'Edycja technika',
  'Zobacz-kalendarz': 'Zobacz kalendarz',
  subscriptions: 'Subskrypcje',
  activities: 'Aktywności',
  qms: 'QMS',
  Profil: 'Profil',
  'Edytuj-profil': 'Edytuj profil',
  'Panel-główny': 'Panel główny',
  Placówki: 'Placówki',
  'Dodaj-placówkę': 'Dodaj placówkę',
  'Edycja-placówki': 'Edycja placówki',
  'Podgląd-placówki': 'Podgląd placówki',
  Plany: 'Plany',
  'Utwórz-plan': 'Utwórz plan',
  'Podgląd-planu': 'Podgląd planu',
  'Edycja-planu': 'Edytuj plan',
  'Edytuj-ofertę': 'Edytuj ofertę',
  'Podgląd-oferty': 'Podgląd oferty',
  'Utwórz-ofertę': 'Utwórz ofertę',
  'Typy-usług': 'Typy usług',
  'Dodaj-typ-usługi': 'Dodaj typ usługi',
  'Podgląd-typu-usługi': 'Podgląd typu usługi',
  'Edycja-typu-usługi': 'Edytuj typ usługi',
  'Zgłoszenia-kontaktowe': 'Zgłoszenia kontaktowe',
  'Utwórz-zgłoszenie-kontaktowe': 'Utwórz zgłoszenie kontaktowe',
  Zgłoszenia: 'Zgłoszenia',
  'Moje-zadania': 'Moje zadania',
  'Podgląd-zgłoszenia': 'Podgląd zgłoszenia serwisowego',
  'Podgląd-zgłoszenia-kontaktowego': 'Podgląd zgłoszenia kontaktowego',
  'Produkty i usługi': 'Produkty i usługi',
  'Formularze-zadań': 'Formularze zadań',
  'Utwórz-formularz': 'Utwórz formularz zadania',
  'Podgląd-Formularza': 'Podgląd formularza zadania',
  'Edytuj-Formularz': 'Edytuj formularz zadania',
  Certyfikaty: 'Certyfikaty',
  'Podgląd-szablonu': 'Podgląd szablonu certyfikatu',
  'Utwórz-szablon-certyfikatu': 'Utwórz szablon certyfikatu',
  'Edycja-szablonu': 'Edytuj szablon certyfikatu',
};

// Maps a parent path to the label used for any unknown (dynamic) child segment.
// Use this when a route like /Parent/:dynamicParam should always show a fixed
// label for the dynamic part instead of the raw URL value.
const dynamicChildLabels: Record<string, string> = {
  '/Produkty i usługi': 'Zamów usługę',
};

// Segments that are part of URL structure but should not appear as breadcrumb items
const skippedSegments = new Set(['krok-1', 'krok-2']);

const getSection = (pathname: string) => pathname.split('/')[1] || '';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isDynamicId = (segment: string | undefined): boolean =>
  Boolean(segment) &&
  (UUID_RE.test(segment as string) ||
    /^[0-9a-f-]{36}$/i.test(segment as string));

/**
 * The role of a page within its section — used to keep List/View/Edit/Add as
 * distinct breadcrumb entries even though they live in the same URL section.
 * Returns 'list' for the section root (e.g. /administrators), and the role
 * segment otherwise (e.g. 'Podgląd-Administratora', 'Edycja-Administratora',
 * 'addUser'). Two entries with the same section AND role are considered the
 * same conceptual page (e.g. viewing two different users) and are collapsed.
 *
 * Most sections follow /section/role/:id, but some (e.g. Placówki) put the id
 * before the role (/section/:id/role). When the segment after the section
 * looks like a UUID, the role is read from the segment after that.
 */
const getPageRole = (pathname: string): string => {
  const segs = pathname.split('/').filter(Boolean);
  if (segs.length <= 1) {
    return 'list';
  }
  if (isDynamicId(segs[1])) {
    return segs[2] ?? 'list';
  }
  return segs[1] as string;
};

/**
 * Builds breadcrumbs from the full navigation history stack.
 * Consecutive entries representing the same conceptual page (same section AND
 * same page role) are collapsed to the deepest path, then breadcrumbs are
 * generated and concatenated across role/section changes. This preserves
 * trails like List → View → Edit while still collapsing list-pagination or
 * same-role transitions between two records.
 */
const buildBreadcrumbsFromStack = (
  stack: { pathname: string; search?: string }[]
): BreadcrumbItem[] => {
  // Step 1: collapse consecutive entries that share both section and role,
  // keeping only the deepest path. List → View → Edit have different roles,
  // so each is preserved as its own crumb.
  // Each collapsed entry stores { pathname, search } so query params are preserved.
  const collapsed: { pathname: string; search: string }[] = [];
  for (const entry of stack) {
    const last = collapsed[collapsed.length - 1];
    const sameAsLast =
      last !== undefined &&
      getSection(entry.pathname) === getSection(last.pathname) &&
      getPageRole(entry.pathname) === getPageRole(last.pathname);
    if (sameAsLast) {
      collapsed[collapsed.length - 1] = {
        pathname: entry.pathname,
        search: entry.search ?? '',
      };
    } else {
      collapsed.push({ pathname: entry.pathname, search: entry.search ?? '' });
    }
  }

  // Build a set of all pathnames directly visited (used to detect cross-section jumps)
  const visitedPathnames = new Set(stack.map((e) => e.pathname));

  // Step 2: generate breadcrumbs per collapsed entry.
  // - Section already seen earlier → skip its parent crumb (avoid duplication).
  // - Cross-section jump (section root never visited) → show only the leaf crumb,
  //   not the section parent (e.g. jumped to /Plany/Podgląd-planu directly from
  //   /Klienci/Podgląd-klienta → show only "Podgląd planu", not "Plany > Podgląd planu").
  const seenSections = new Set<string>();
  return collapsed.flatMap(({ pathname, search }) => {
    const fullPath = pathname + search;
    const section = getSection(pathname);
    const sectionRoot = `/${section}`;
    const crumbs = generateBreadcrumbs(fullPath);

    let included: BreadcrumbItem[];
    if (seenSections.has(section)) {
      // Section already shown — drop its parent crumb
      included = crumbs.slice(1);
    } else if (pathname !== sectionRoot && !visitedPathnames.has(sectionRoot)) {
      // Cross-section jump — show only the leaf crumb
      included = crumbs.slice(-1);
    } else {
      included = crumbs;
    }
    seenSections.add(section);
    // The leaf crumb navigates to the full stack path (preserves UUID + search).
    // Parent crumbs use their own partial path (e.g. /Klienci for the list).
    return included.map((crumb, i) => ({
      ...crumb,
      navigateTo: i === included.length - 1 ? fullPath : crumb.path,
    }));
  });
};

/**
 * Generates breadcrumb items from a pathname
 */
const generateBreadcrumbs = (pathname: string): BreadcrumbItem[] => {
  // Remove query params and decode URL
  const queryIndex = pathname.indexOf('?');
  const pathWithoutQuery =
    queryIndex !== -1 ? pathname.substring(0, queryIndex) : pathname;
  const decodedPath = decodeURIComponent(pathWithoutQuery);

  // Split into segments and filter out empty strings
  const segments = decodedPath
    .split('/')
    .filter((seg): seg is string => Boolean(seg));

  if (segments.length === 0) {
    return [{ label: 'Panel główny', path: '/', navigateTo: '/' }];
  }

  const breadcrumbs: BreadcrumbItem[] = [];

  // Build breadcrumbs by accumulating path segments
  let currentPath = '';
  for (const segment of segments) {
    if (!segment) {
      continue;
    }

    currentPath += `/${segment}`;

    // Skip dynamic segments (UUIDs, IDs, etc.)
    // Check if segment looks like a UUID or ID (contains only hex chars or is very long)
    const isDynamicSegment =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        segment
      ) || /^[0-9a-f-]{36}$/i.test(segment);

    if (isDynamicSegment || skippedSegments.has(segment)) {
      continue;
    }

    // When building the /Zgłoszenia parent crumb from a contact-request detail
    // path, point it to the kontaktowe tab so the back-link lands correctly.
    const isContactRequestDetail = decodedPath.includes(
      'Podgląd-zgłoszenia-kontaktowego'
    );
    const crumbPath =
      currentPath === '/Zgłoszenia' && isContactRequestDetail
        ? '/Zgłoszenia?tab=kontaktowe'
        : currentPath;

    // Try to get label from full path first (for exact matches)
    const fullPathLabel = routeLabels[currentPath];
    if (fullPathLabel) {
      breadcrumbs.push({
        label: fullPathLabel,
        path: crumbPath,
        navigateTo: crumbPath,
      });
    } else {
      // Fall back to segment label, then to a parent-scoped dynamic label,
      // then to the raw segment value.
      const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/'));
      const segmentLabel =
        segmentLabels[segment] || dynamicChildLabels[parentPath] || segment;
      breadcrumbs.push({
        label: segmentLabel,
        path: crumbPath,
        navigateTo: crumbPath,
      });
    }
  }

  return breadcrumbs.length > 0
    ? breadcrumbs
    : [{ label: 'Panel główny', path: '/', navigateTo: '/' }];
};

export type LayOutHeaderType = {
  headerStyle: CSSProperties;
  isCollapsed: boolean;
  toggleCollapse: () => void;
};

const LayOutHeader: React.FC<LayOutHeaderType> = ({
  headerStyle,
  toggleCollapse,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { stack } = useNavigationHistory();
  const decodedPathname = decodeURIComponent(location.pathname);
  const breadcrumbs =
    decodedPathname === '/Panel-główny'
      ? [
          {
            label: 'Panel główny',
            path: '/Panel-główny',
            navigateTo: '/Panel-główny',
          },
        ]
      : decodedPathname === '/Profil/Edytuj-profil'
        ? [
            { label: 'Profil', path: '/Profil', navigateTo: '/Profil' },
            {
              label: 'Edytuj profil',
              path: '/Profil/Edytuj-profil',
              navigateTo: '/Profil/Edytuj-profil',
            },
          ]
        : decodedPathname.startsWith('/Profil')
          ? [{ label: 'Profil', path: '/Profil', navigateTo: '/Profil' }]
          : buildBreadcrumbsFromStack(stack);

  const handleBreadcrumbClick = (crumb: BreadcrumbItem, isLast: boolean) => {
    if (!isLast) {
      const targetBasePath = crumb.navigateTo.split('?')[0];
      if (crumb.navigateTo === '/administrators') {
        navigate('/administrators?page=1&limit=10', { replace: true });
      } else if (
        targetBasePath === '/Zgłoszenia' &&
        decodedPathname.includes('Podgląd-zgłoszenia-kontaktowego')
      ) {
        navigate('/Zgłoszenia?tab=kontaktowe');
      } else {
        navigate(crumb.navigateTo);
      }
    }
  };

  return (
    <>
      <div
        className="fixed top-0 transition-all duration-300 flex items-center justify-between bg-white"
        style={{
          ...headerStyle,
          height: LAYOUT_CONSTANTS.HEADER_HEIGHT,
          zIndex: 40,
          paddingTop: 4,
          paddingBottom: 4,
          paddingLeft: 4,
          paddingRight: 20,
        }}
      >
        <div className="flex items-center">
          <div className="flex items-center">
            <button
              onClick={toggleCollapse}
              className="w-12 h-12 flex items-center justify-center cursor-pointer"
              aria-label="Toggle menu"
            >
              <div className="w-10 overflow-hidden rounded-xl flex flex-col items-center justify-center">
                <div className="self-stretch h-10 flex items-center justify-center">
                  <MenuIcon />
                </div>
              </div>
            </button>
          </div>
          <div className="flex flex-col items-start">
            <div className="px-3 py-1.5 flex items-center justify-center">
              <div className="self-stretch overflow-hidden rounded flex items-center justify-center">
                <div className="flex items-center justify-center gap-1">
                  <div className="flex items-center gap-2 text-[#474A4C] text-xs font-normal leading-4 tracking-[0.4px]">
                    {breadcrumbs.map((crumb, index) => {
                      const isLast = index === breadcrumbs.length - 1;
                      return (
                        <React.Fragment key={index}>
                          <button
                            type="button"
                            onClick={() => handleBreadcrumbClick(crumb, isLast)}
                            disabled={isLast}
                            data-testid={`breadcrumb-${crumb.label}`}
                            className={`${
                              isLast
                                ? 'cursor-default'
                                : 'cursor-pointer text-primary-color-40'
                            }`}
                          >
                            {crumb.label}
                          </button>
                          {index < breadcrumbs.length - 1 && (
                            <ChevronRightIcon className="w-4 h-4 mx-1 text-on-surface-variant" />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <UserProfileDropdown />
      </div>
    </>
  );
};

export default LayOutHeader;
